import React from 'react';
import { withBlockExtensions } from '@plone/volto/helpers';
import { SidebarPortal } from '@plone/volto/components';
import {
  ONDE_ATUA_SLOTS,
  minVisibleSlots,
} from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';

import View from './View';
import Data from './Data';
import { temaFieldIds } from './schema';

/**
 * Editor do bloco "Onde o MPF atua?".
 *
 * O estado do item aberto é único e mora aqui — ver `Destaques/Edit.tsx`. A
 * diferença é que aqui há TRÊS listas de cards (uma por tema), então o estado
 * guarda o par `{ tema, index }`: só a lista do tema em foco recebe o índice,
 * as outras duas recebem `-1` (fechado, mas ainda controlado).
 *
 * A sidebar mostra os temas até o último com dado (`minVisibleSlots`); os
 * seguintes entram pelo botão "Adicionar tema". `revealed` é só estado de
 * tela — nada é gravado no bloco, então blocos já publicados abrem com os
 * temas que têm.
 */
const Edit = (props) => {
  const { block, data, onChangeBlock, onSelectBlock, selected } = props;
  const [active, setActive] = React.useState({ tema: 0, index: -1 });
  const [revealed, setRevealed] = React.useState(0);
  const visibleSlots = Math.min(
    ONDE_ATUA_SLOTS.length,
    Math.max(minVisibleSlots(data), revealed),
  );

  const scrollToTema = (tema: number) =>
    window.requestAnimationFrame(() => {
      document
        .getElementById(`blockform-fieldset-tema${tema}`)
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    });

  const handleEditItem = React.useCallback(
    (tema: number, index: number) => {
      onSelectBlock?.(block);
      setActive({ tema, index });
      // A sidebar mostra os temas empilhados; sem rolar até o certo, o item
      // aberto pode ficar fora da área visível. Depois do próximo paint, para
      // o acordeão já estar com o item expandido.
      scrollToTema(tema);
    },
    [block, onSelectBlock],
  );

  const setActiveObject = React.useCallback(
    (tema: number, index: number) => setActive({ tema, index }),
    [],
  );

  const handleAddTema = () => {
    const tema = visibleSlots + 1;
    setRevealed(tema);
    scrollToTema(tema);
  };

  // Limpa os campos do slot sem puxar os seguintes para o lugar dele: mexer
  // na posição reescreveria dados de outros temas já publicados.
  const handleRemoveTema = (tema: number) => {
    const next = { ...data };
    Object.values(temaFieldIds(tema)).forEach((id) => delete next[id]);
    onChangeBlock(block, next);
    setRevealed(tema - 1);
    if (active.tema === tema) setActive({ tema: 0, index: -1 });
  };

  return (
    <>
      <View {...props} data={data} onEditItem={handleEditItem} />

      <SidebarPortal selected={selected}>
        <Data
          {...props}
          activeObjects={{ [active.tema]: active.index }}
          setActiveObject={setActiveObject}
          visibleSlots={visibleSlots}
          onAddTema={handleAddTema}
          onRemoveTema={handleRemoveTema}
        />
      </SidebarPortal>
    </>
  );
};

export default withBlockExtensions(Edit);
